import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ContinueToPaymentsArrow from '../../../assets/next-beige.svg';
import ContinueStep from '../../../assets/arrow-down-white.svg';

import TouchableDiv from '../../common/TouchableDiv';
import FigoLottie from '../../common/FigoLottie';
import { PurchaseStickyBar } from './PurchaseStickyBar';

import {
  validateDiamondUser,
  validateRecaptchaToken,
} from '../../../actions';
import {
  URL_PURCHASE,
  URL_PURCHASE_ERROR,
  URL_PURCHASE_SUCCESS_ERROR,
  URL_ROOT_QUOTE,
} from '../../../routes';
import {
  usePurchase,
  useRecaptchaScript,
  useSavedElement,
} from '../../../hooks';

import './YourInfoFooter.css';
import { sendKameleoonPurchaseEvent } from '../../../utils/kameleoon.utils';
import { sendEverflowPolicy } from '../../../utils/everflow.utils';

const ORIGIN_EB = 2;

const YourInfoFooter = ({
  handleContinuePurchaseClick = () => { },
  isPurchaseValid = false,
  isSecondStep = false,
  petCloudLoginSuccess,
}) => {
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const history = useHistory();
  const dispatch = useDispatch();
  const { doPurchase } = usePurchase();
  const { saveId } = useSavedElement();
  const {
    customerValidationLoading,
    data,
    parameters,
    petsWithQuoteError,
    purchase,
    quoteSelectedValues,
    recaptcha,
    searchParameters,
    sessionInformation,
    stripe: { paymentMethod },
  } = store;
  const { userEmail } = sessionInformation;

  const scriptRecaptchaLoaded = useRecaptchaScript();

  const sendEverflowPolicyEventPerPet = useCallback(async () => {
    if (purchase.policyNumbers.length) {
      purchase.policyNumbers.forEach((policyPurchased) => {
        const petPurchased = quoteSelectedValues
          .find((quote) => quote.quoteId === policyPurchased.PetQuoteId);
        const amount = petPurchased?.total || 0;

        sendEverflowPolicy({
          amount,
          groupCode: data?.groupCode || parameters.groupCode,
          policyId: policyPurchased.PolicyNumber,
        });
      });
    }
  }, [data, parameters, purchase, quoteSelectedValues]);

  useEffect(() => {
    if (purchase.error && !purchase.success) {
      history.push(URL_PURCHASE_ERROR);
    }

    if (!purchase.error && purchase.success) {
      const policiesTotals = purchase.policyNumbers
        .map((policyPurchased) => {
          const petTotal = quoteSelectedValues
            .find((quote) => quote.quoteId === policyPurchased.PetQuoteId);
          return petTotal?.total || 0;
        });
      const quoteTotal = policiesTotals
        .reduce((acc, currentValue) => acc + currentValue, 0);
      if (quoteTotal > 0) {
        sendKameleoonPurchaseEvent(quoteTotal);
      }

      sendEverflowPolicyEventPerPet();

      const goToPurchaseScreen = async () => {
        const url = purchase.incompletePurchases?.length > 0
          ? URL_PURCHASE_SUCCESS_ERROR : URL_PURCHASE;

        history.push(url);
      };

      goToPurchaseScreen();
    }
  }, [
    dispatch,
    history,
    purchase,
    sendEverflowPolicyEventPerPet,
    quoteSelectedValues,
  ]);

  const handleReturnToPlansClick = () => {
    const url = `${URL_ROOT_QUOTE}${searchParameters}`;
    history.push(url);
  };

  function renderContentButton() {
    const title = isSecondStep ? 'Purchase' : 'Continue';
    const className = isSecondStep ? 'Your-info-continue-to-payments-arrow'
      : 'Your-info-continue-to-step-arrow';
    const iconStep = isSecondStep ? ContinueToPaymentsArrow : ContinueStep;

    if (customerValidationLoading || recaptcha.loading) {
      return (
        <FigoLottie
          height={30}
          width={30}
        />
      );
    }

    return (
      <>
        <span className="Your-info-continue-to-payments-label">{title}</span>

        <img
          alt=""
          className={className}
          src={iconStep}
        />
      </>
    );
  }

  const handleOnPurchaseQuote = async () => {
    const {
      newCustomer,
    } = store;
    const { email, emailValidated } = newCustomer;

    if (petsWithQuoteError.length) {
      window.scrollTo(0, 0);
      return;
    }

    if (!emailValidated) {
      dispatch(validateDiamondUser({
        email,
      }));
      return;
    }

    await handleContinuePurchaseClick?.();

    if (!isSecondStep) {
      // prepare focus for "continue" button disappearing
      saveId('billing-cycle-title');
    }

    if (!isSecondStep && !petCloudLoginSuccess) {
      return;
    }

    if (!isSecondStep || !isPurchaseValid) {
      return;
    }

    if (!isPurchaseValid || store.purchase.loading) {
      return;
    }

    const tokenId = paymentMethod?.token;
    if (tokenId) {
      doPurchase();
    }
  };

  async function onSubmit(token) {
    const { loading } = recaptcha;

    if (loading) {
      return;
    }

    const isValidToken = await dispatch(validateRecaptchaToken({
      origin: ORIGIN_EB,
      token,
      user: userEmail,
    }));

    if (isValidToken) {
      handleOnPurchaseQuote();
    }
  }

  const handleSubmit = () => {
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(process.env.REACT_APP_RECAPTCHA_KEY, { action: 'submit' })
          .then(onSubmit);
      });
    }
  };

  function renderContinuePurchasePaymentButton() {
    if (isSecondStep && !paymentMethod?.token) {
      return null;
    }

    if (!scriptRecaptchaLoaded) {
      return null;
    }

    return isSecondStep
      ? (
        <button
          className="Your-info-continue-to-payments-button"
          id="continueToPaymentsBtn"
          onClick={handleOnPurchaseQuote}
          type="button"
        >
          {renderContentButton()}
        </button>
      )
      : (
        <button
          className="Your-info-continue-to-payments-button"
          id="continueToPaymentsBtn"
          onClick={handleSubmit}
          type="submit"
        >
          {renderContentButton()}
        </button>
      );
  }

  if (isSecondStep) {
    return (
      <PurchaseStickyBar
        disabled={!isPurchaseValid}
        onPurchaseClick={isSecondStep
          ? handleOnPurchaseQuote : handleContinuePurchaseClick}
      />
    );
  }

  return (
    <div
      className={'Your-info-footer-container'
        + ' Your-info-footer-container-enrollment'}
    >
      <div className="Your-info-footer-buttons-container">
        <TouchableDiv
          className={'Your-info-return-to-plans-button'
            + ' Your-info-return-to-plans-button-enrollment'}
          id="returnToPlansBtn"
          onClick={handleReturnToPlansClick}
        >
          <span
            aria-hidden="true"
            className="Your-info-return-to-plans-label"
          >
            ↵ &nbsp;
          </span>

          <span className="Your-info-return-to-plans-label">
            Back to Customize
          </span>
        </TouchableDiv>

        {renderContinuePurchasePaymentButton()}
      </div>
    </div>
  );
};

export { YourInfoFooter };
